/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/MailboxSection.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-MAILBOX-001
 * PURPOSE: Renders mailbox visibility filters and refresh behavior.
 */
import { useEffect } from 'react'
import { Inbox, X } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { EmailFacets } from '../../hooks/useEmails'
import { mailboxColor } from '../../lib/colorPalettes'
import { mailboxKey } from '../../stores/emailFilterStore'
import { SectionHeader } from './SidebarChrome'
import type { SectionProps } from './types'

interface MailboxSectionProps extends SectionProps {
  facets: EmailFacets | null
  selectedKeys: string[]
  onChange: (keys: string[]) => void
  onReload: () => void
}

export function MailboxSection({
  expanded,
  onToggle,
  facets,
  selectedKeys,
  onChange,
  onReload,
}: MailboxSectionProps) {
  const mailboxes = facets?.mailboxes || []
  // Refresh facets the first time the user opens this section so the
  // dropdown is fresh after sync events.
  useEffect(() => {
    if (expanded) onReload()
  }, [expanded, onReload])

  const toggleKey = (key: string) => {
    const set = new Set(selectedKeys)
    if (set.has(key)) set.delete(key)
    else set.add(key)
    onChange(Array.from(set))
  }

  return (
    <div className="mb-3">
      <SectionHeader
        label="Mailbox"
        expanded={expanded}
        onToggle={onToggle}
        count={selectedKeys.length}
      />
      {expanded && (
        <div className="space-y-0.5">
          {mailboxes.length === 0 ? (
            <p className="text-[10px] text-muted-foreground px-2 italic">
              No mailboxes connected.
            </p>
          ) : (
            mailboxes.map((mb) => {
              const key = mailboxKey({
                account_id: mb.account_id,
                user_id: mb.user_id,
              })
              if (key == null) return null
              const c = mailboxColor(mb.email)
              const active = selectedKeys.includes(key)
              // Legacy buckets (no account) carry older, un-attributed
              // mail — label them so they're not mistaken for a live
              // mailbox the user can sync.
              const isLegacy = mb.account_id == null
              return (
                <button
                  key={key}
                  onClick={() => toggleKey(key)}
                  className={cn(
                    'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
                    active
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-accent',
                  )}
                  title={isLegacy ? `${mb.email} - older mail (not attributed to a mailbox)` : mb.email}
                >
                  <span className={cn('h-2 w-2 rounded-full shrink-0', c.dot)} />
                  <Inbox className="h-3 w-3 shrink-0" />
                  <span className="flex-1 truncate">
                    {mb.name || mb.email.split('@')[0]}
                    {isLegacy && (
                      <span className="ml-1 opacity-60">(older)</span>
                    )}
                  </span>
                  <span className="text-[9px] tabular-nums opacity-70">{mb.count}</span>
                </button>
              )
            })
          )}
          {selectedKeys.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-2 py-1 text-[10px] text-muted-foreground hover:underline"
            >
              <X className="h-2.5 w-2.5 inline" /> Show all mailboxes
            </button>
          )}
        </div>
      )}
    </div>
  )
}
